import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Info,
  KeyRound,
  Share2,
  TrendingDown,
  Wallet,
  Building2,
  PieChart,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const SALARY_MIN = 20000;
const SALARY_MAX = 200000;
const POPULAR_SALARIES = [
  20000, 25000, 30000, 35000, 40000, 45000, 50000, 60000, 70000, 80000, 90000, 100000, 120000,
  150000, 200000,
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);

const formatMonthly = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const toPath = (salary: number) => `/salary-after-tax/${salary}-ireland`;

// Calculation Logic
const calculatePaye = (salary: number): number => {
  const standardBand = 44000;
  const taxCredits = 4000;
  const at20 = Math.min(salary, standardBand);
  const at40 = Math.max(salary - standardBand, 0);
  const grossTax = at20 * 0.2 + at40 * 0.4;
  return Math.max(Math.round(grossTax - taxCredits), 0);
};

const calculateUsc = (salary: number): number => {
  const bands = [
    { cap: 12012, rate: 0.005 },
    { cap: 15370, rate: 0.02 },
    { cap: 42662, rate: 0.03 },
    { cap: Infinity, rate: 0.08 },
  ];
  let remaining = salary;
  let usc = 0;
  for (const band of bands) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, band.cap);
    usc += taxable * band.rate;
    remaining -= taxable;
  }
  return Math.round(usc);
};

const calculatePrsi = (salary: number): number => Math.round(salary * 0.04);

const SalaryAfterTaxDiscoverPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [calculatorSalary, setCalculatorSalary] = useState(45000);
  const [calculatorInput, setCalculatorInput] = useState('45000');

  const normalized = query.replace(/[^\d]/g, '');
  const queryNumber = normalized ? Number(normalized) : 0;

  const matches = useMemo(() => {
    if (!query.trim()) return POPULAR_SALARIES;
    if (!queryNumber) return [];
    return POPULAR_SALARIES;
  }, [query, queryNumber]);

  const calc = useMemo(() => {
    const salary = Math.min(Math.max(calculatorSalary, SALARY_MIN), SALARY_MAX);
    const paye = calculatePaye(salary);
    const usc = calculateUsc(salary);
    const prsi = calculatePrsi(salary);
    const totalDeductions = paye + usc + prsi;
    const netAnnual = salary - totalDeductions;
    const netMonthly = netAnnual / 12;
    const taxPercent = (totalDeductions / salary) * 100;

    return {
      salary,
      paye,
      usc,
      prsi,
      totalDeductions,
      netAnnual,
      netMonthly,
      taxPercent,
      recommendedRent: Math.round(netMonthly * 0.35),
      maxMortgage: Math.round(salary * 4),
      buyBudget: Math.round(salary * 4 * 1.1),
    };
  }, [calculatorSalary]);
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${toPath(calc.salary)}`
      : toPath(calc.salary);
  const shareTitle = `Check take-home pay for ${formatCurrency(calc.salary)} in Ireland`;

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible min-h-screen bg-cover bg-center bg-no-repeat font-sans">
      <Helmet>
        <title>Salary After Tax Ireland | Net Pay Calculator (2026)</title>
      </Helmet>

      {/* --- HERO SECTION --- */}
      <div className="bg-slate-900 pt-16 pb-32 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-emerald-500/10 blur-[120px] rounded-full -mr-20" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-4 py-1.5 rounded-full text-emerald-400 text-xs font-black uppercase tracking-widest mb-8">
            <ShieldCheck className="h-4 w-4" /> RENTSMART.IE
          </div>
          <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight">
            Ireland Salary <span className="text-emerald-500">After Tax</span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 font-medium">
            Instantly calculate your take-home pay and discover what your salary buys you in the
            current Irish property market.
          </p>
        </div>
      </div>

      {/* --- MAIN CALCULATOR DASHBOARD --- */}
      <main className="max-w-6xl mx-auto px-4 -mt-16 relative z-20">
        <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/60 border border-slate-100 p-6 md:p-10">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* LEFT: Input & Visualization */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 block">
                  Adjust Gross Annual Salary
                </label>
                <div className="relative">
                  <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-black text-slate-300">
                    €
                  </span>
                  <input
                    type="text"
                    value={calculatorInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d]/g, '');
                      setCalculatorInput(val);
                      if (val) setCalculatorSalary(Number(val));
                    }}
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl pl-12 pr-6 py-5 text-3xl font-black text-slate-900 focus:border-emerald-500 focus:bg-white transition-all outline-none"
                  />
                </div>
                <input
                  type="range"
                  min={SALARY_MIN}
                  max={SALARY_MAX}
                  step={500}
                  value={calc.salary}
                  onChange={(e) => {
                    setCalculatorSalary(Number(e.target.value));
                    setCalculatorInput(e.target.value);
                  }}
                  className="w-full mt-6 h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
              </div>

              {/* RETENTION GAUGE */}
              <div className="bg-slate-900 rounded-3xl p-8 text-white flex items-center justify-between overflow-hidden relative">
                <div className="relative z-10">
                  <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-1">
                    Retention Rate
                  </p>
                  <div className="text-4xl font-black">{(100 - calc.taxPercent).toFixed(1)}%</div>
                  <p className="text-slate-400 text-xs mt-1">Take-home pay after tax</p>
                </div>
                <div className="relative h-24 w-24">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      className="text-slate-800"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={251.2}
                      strokeDashoffset={251.2 - (251.2 * (100 - calc.taxPercent)) / 100}
                      className="text-emerald-500 transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <PieChart className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </div>

            {/* RIGHT: Detailed Breakdown */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 flex items-center justify-between mb-2">
                  <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-emerald-500" /> Monthly Breakdown
                  </h2>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                    Tax Year 2026
                  </span>
                </div>

                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
                  <p className="text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-1">
                    Net Monthly Take-home
                  </p>
                  <div className="text-3xl font-black text-emerald-900">
                    {formatMonthly(calc.netMonthly)}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">
                    Net Annual Take-home
                  </p>
                  <div className="text-3xl font-black text-slate-900">
                    {formatCurrency(calc.netAnnual)}
                  </div>
                </div>

                {/* DEDUCTIONS LIST */}
                <div className="md:col-span-2 bg-white border border-slate-100 rounded-3xl p-2 space-y-1">
                  {[
                    {
                      label: 'PAYE Income Tax',
                      val: calc.paye,
                      icon: <TrendingDown className="h-4 w-4 text-rose-500" />,
                    },
                    {
                      label: 'USC (Universal Social Charge)',
                      val: calc.usc,
                      icon: <TrendingDown className="h-4 w-4 text-amber-500" />,
                    },
                    {
                      label: 'PRSI (Social Insurance)',
                      val: calc.prsi,
                      icon: <TrendingDown className="h-4 w-4 text-orange-500" />,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-100">
                          {item.icon}
                        </div>
                        <span className="font-bold text-slate-600 text-sm">{item.label}</span>
                      </div>
                      <span className="font-black text-slate-900">{formatCurrency(item.val)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-4 bg-rose-50/50 rounded-2xl mt-2 border border-rose-100/50">
                    <span className="font-black text-rose-700 text-sm uppercase tracking-widest">
                      Total Annual Tax
                    </span>
                    <span className="font-black text-rose-700 text-xl">
                      {formatCurrency(calc.totalDeductions)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- IMPROVED SHARE SECTION --- */}
          <div className="mt-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-1 shadow-sm transition-all hover:shadow-md">
            <div className="flex flex-col items-center justify-between gap-6 px-6 py-5 sm:flex-row md:px-8">
              {/* Left: Icon & Text Context */}
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                  <Share2 className="h-5 w-5" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400 mb-0.5">
                    Spread the word
                  </p>
                  <p className="font-bold leading-tight text-slate-900">
                   Tell your network what your {formatCurrency(calc.salary)} salary looks like after tax in Ireland, and help them understand their own take-home pay! 
                    <span className="text-emerald-600">{' '}{formatCurrency(calc.salary)}</span>
                  </p>
                </div>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                {/* Social Icons Container */}
                <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-1.5 border border-slate-100">
                  <FacebookShareButton
                    url={shareUrl}
                    className="transition-transform hover:-translate-y-1 active:scale-95"
                  >
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>

                  <TwitterShareButton
                    url={shareUrl}
                    title={shareTitle}
                    className="transition-transform hover:-translate-y-1 active:scale-95"
                  >
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>

                  <WhatsappShareButton
                    url={shareUrl}
                    title={shareTitle}
                    className="transition-transform hover:-translate-y-1 active:scale-95"
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>

                  <LinkedinShareButton
                    url={shareUrl}
                    className="transition-transform hover:-translate-y-1 active:scale-95"
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>

                {/* Copy Link Action */}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    // Optional: Add a toast notification here
                    alert('Link copied to clipboard!');
                  }}
                  className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-black text-slate-600 transition-all hover:border-emerald-500 hover:text-emerald-600 active:bg-slate-50"
                >
                  <ArrowRight className="h-4 w-4" />
                  Copy Link
                </button>
              </div>
            </div>
          </div>

          <hr className="my-10 border-slate-100" />

          {/* PROPERTY INSIGHTS BENTO BOX */}
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href={`/browse?listingIntent=rent&maxPrice=${calc.recommendedRent}`}
              className="group no-underline relative overflow-hidden bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-[2rem] p-8 text-white transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-emerald-900/20"
            >
              <KeyRound className="absolute -right-4 -bottom-4 h-32 w-32 text-white/10 rotate-12" />
              <div className="relative z-10">
                <p className="text-emerald-200 text-xs font-black uppercase tracking-widest mb-2">
                  Rental Affordability
                </p>
                <h3 className="text-3xl font-black mb-4">
                  Rent for {formatCurrency(calc.recommendedRent)}/mo
                </h3>
                <p className="text-emerald-100/80 text-sm font-medium mb-8 max-w-xs leading-relaxed">
                  Based on the 35% rule of thumb for financial health in Ireland.
                </p>
                <div className="inline-flex items-center gap-2 bg-white text-emerald-800 px-5 py-3 rounded-xl font-black text-sm uppercase tracking-tighter">
                  See Available Rentals <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </a>

            <a
              href={`/browse?listingIntent=sale&location=Dublin&maxPrice=${calc.buyBudget}`}
              className="group no-underline relative overflow-hidden bg-gradient-to-br from-sky-600 to-sky-800 rounded-[2rem] p-8 text-white transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-sky-900/20"
            >
              <Building2 className="absolute -right-4 -bottom-4 h-32 w-32 text-white/10 -rotate-12" />
              <div className="relative z-10">
                <p className="text-sky-200 text-xs font-black uppercase tracking-widest mb-2">
                  Buying Benchmark
                </p>
                <h3 className="text-3xl font-black mb-4">
                  Buy for {formatCurrency(calc.buyBudget)}
                </h3>
                <p className="text-sky-100/80 text-sm font-medium mb-8 max-w-xs leading-relaxed">
                  Estimated purchase power using 4x gross salary + 10% deposit.
                </p>
                <div className="inline-flex items-center gap-2 bg-white text-sky-800 px-5 py-3 rounded-xl font-black text-sm uppercase tracking-tighter">
                  View Property Market <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* --- DISCOVER SECTION --- */}
        <section className="mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 px-4 gap-4">
            <div>
              <h2 className="text-3xl font-black text-slate-900 mb-2">Compare Salaries</h2>
              <p className="text-slate-500 font-medium">
                Browse net-pay reports for{' '}
                {query.trim() ? `results near €${query}` : 'popular Irish salary bands'}.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {matches.map((salary) => (
              <Link
                key={salary}
                to={toPath(salary)}
                className="group no-underline bg-white border border-slate-200 p-6 rounded-3xl hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1 group-hover:text-emerald-600 transition-colors">
                  Gross Annual
                </p>
                <div className="text-2xl font-black text-slate-900 mb-4">
                  {formatCurrency(salary)}
                </div>
                <div className="flex items-center justify-between text-xs font-bold text-slate-500 group-hover:text-slate-900 transition-colors">
                  Check Report{' '}
                  <ArrowRight className="h-4 w-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
              </Link>
            ))}
          </div>

          {matches.length === 0 && (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2rem] p-12 text-center text-slate-400 font-bold">
              No salary pages found for "{query}". Try entering a number between 20k and 200k.
            </div>
          )}
        </section>

        {/* --- DISCLAIMER --- */}
        <footer className="mt-20 bg-slate-100 rounded-[2.5rem] p-8 md:p-12 border border-slate-200/50">
          <div className="flex gap-4">
            <Info className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-slate-900 font-black mb-2 uppercase tracking-tighter">
                Important Notice
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                This calculator provides estimates based on 2026 Irish tax bands (PAYE, USC, and
                PRSI). It assumes a single person with standard tax credits (€4,000). Your actual
                take-home pay may vary based on pension contributions, health insurance, marital
                status, or additional tax credits.
              </p>
              <p className="text-sm text-slate-500 font-bold">
                RentSmart.ie is a property research platform and does not provide financial or tax
                advice.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default SalaryAfterTaxDiscoverPage;
